import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import './ConfirmModal.css';

export type ConfirmModalProps = {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** When set, the confirm button is disabled until the user types this exact string. */
  requireType?: string;
  /** Disables the confirm button while the caller's action is in flight (no double submit). */
  busy?: boolean;
};

export function ConfirmModal({
  title,
  body,
  confirmLabel,
  cancelLabel,
  danger = false,
  onConfirm,
  onCancel,
  requireType,
  busy = false,
}: ConfirmModalProps) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');
  const titleId = useId();
  const bodyId = useId();
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const typeInputRef = useRef<HTMLInputElement>(null);

  const canConfirm = (!requireType || typed === requireType) && !busy;

  useEffect(() => {
    const target = requireType ? typeInputRef.current : cancelButtonRef.current;
    target?.focus();
  }, [requireType]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onCancel]);

  const modal = (
    <div
      className="confirm-modal__backdrop"
      role="presentation"
      onClick={onCancel}
      data-testid="confirm-modal-backdrop"
    >
      <div
        className="confirm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={body ? bodyId : undefined}
        onClick={(event) => event.stopPropagation()}
      >
        <h3 id={titleId} className="confirm-modal__title">
          {title}
        </h3>
        {body && (
          <p id={bodyId} className="confirm-modal__body">
            {body}
          </p>
        )}
        {requireType && (
          <div className="confirm-modal__type">
            <div className="confirm-modal__type-label">
              {t('confirmModal.typeToConfirm', 'Type')}{' '}
              <strong className="confirm-modal__type-token">{requireType}</strong>{' '}
              {t('confirmModal.toConfirm', 'to confirm:')}
            </div>
            <input
              ref={typeInputRef}
              type="text"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder={requireType}
              className="confirm-modal__type-input"
              aria-label={t('confirmModal.typeToConfirmAria', 'Confirmation phrase')}
            />
          </div>
        )}
        <div className="confirm-modal__actions">
          <button
            ref={cancelButtonRef}
            type="button"
            onClick={onCancel}
            className="confirm-modal__btn confirm-modal__btn--cancel"
          >
            {cancelLabel ?? t('confirmModal.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            className={`confirm-modal__btn confirm-modal__btn--confirm${danger ? ' is-danger' : ''}`}
          >
            {confirmLabel ?? t('confirmModal.confirm', 'Confirm')}
          </button>
        </div>
      </div>
    </div>
  );

  // Render through a portal anchored on document.body so the modal escapes
  // every ancestor stacking context — notably the sticky AdminLayout sidebar
  // (which traps SignOutButton's modal behind the page's Select dropdowns),
  // the fixed mobile sidebar drawer, and any transformed parent. SSR-safe
  // fallback returns the inline tree when `document` is undefined.
  return typeof document === 'undefined' ? modal : createPortal(modal, document.body);
}
